import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Chip,
  Tabs, Tab, FormControlLabel, Checkbox, FormGroup, FormControl,
  InputLabel, Select, MenuItem, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAppContext } from '../../context/AppContext';
import axios from 'axios';

const UserList = () => {
  // Get query parameters
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const userIdFromUrl = queryParams.get('id');

  // Use the shared context
  const {
    users,
    loading,
    error,
    getUserProjects,
    projectUsers,
    deleteUser,
    projects,
    assignUserToProject,
    removeUserFromProject
  } = useAppContext();

  const [openDialog, setOpenDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: '',
    email: '',
    hourly_rate: 0,
    role: 'quantity_surveyor',
    permissions: 'user',
    is_project_lead: false,
    department_id: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [departments, setDepartments] = useState([]);

  // State for assign project dialog
  const [openAssignProjectDialog, setOpenAssignProjectDialog] = useState(false);
  const [newProjectAssignment, setNewProjectAssignment] = useState({
    project_ids: [] // Changed to array for multiple selections
  });
  const [jobNumberInput, setJobNumberInput] = useState('');

  // Function to handle tab changes
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const [isEditing, setIsEditing] = useState(false);

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get('/api/departments', { withCredentials: true });
        setDepartments(response.data);
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  // Find the user from URL parameter
  useEffect(() => {
    if (userIdFromUrl && users.length > 0) {
      const user = users.find(u => u.id === parseInt(userIdFromUrl) || u.id === userIdFromUrl);
      if (user) {
        setSelectedUser(user);
      }
    }
  }, [userIdFromUrl, users]);

  // Fetch user projects when a user is selected or when projectUsers changes
  useEffect(() => {
    if (selectedUser && getUserProjects) {
      const projects = getUserProjects(selectedUser.id);
      setUserProjects(projects || []);
    }
  }, [selectedUser, getUserProjects, projectUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setCurrentUser({
        ...user,
        is_project_lead: user.project_lead === 1 || user.is_project_lead || false, // Map project_lead from backend
        department_id: user.department_id || ''
      });
      setIsEditing(true);
    } else {
      setCurrentUser({
        name: '',
        email: '',
        hourly_rate: 0,
        role: 'Trainee Quantity Surveyor',
        is_project_lead: false,
        department_id: ''
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;

    if (type === 'checkbox') {
      // For checkbox inputs, use the checked property
      setCurrentUser({
        ...currentUser,
        [name]: checked
      });
    } else {
      // For other inputs, use the value property
      let processedValue = value;
      
      // Handle specific field types
      if (name === 'hourly_rate') {
        processedValue = parseFloat(value);
      } else if (name === 'department_id') {
        processedValue = value === '' ? null : parseInt(value);
      }

      setCurrentUser({
        ...currentUser,
        [name]: processedValue
      });
    }
  };

  const handleSubmit = async () => {
    try {
      // Prepare user data including project_lead field
      const userData = {
        ...currentUser,
        project_lead: currentUser.is_project_lead // Map is_project_lead to project_lead for backend
      };

      // Remove the frontend-specific field
      delete userData.is_project_lead;

      // Ensure department_id is properly formatted for the database
      if (userData.department_id === '' || userData.department_id === 0) {
        userData.department_id = null;
      }

      console.log('Submitting user data:', userData);
      console.log('Is editing:', isEditing);
      console.log('User ID:', currentUser.id);

      if (isEditing) {
        console.log('Making PUT request to:', `/api/auth0/users/${currentUser.id}`);
        const response = await axios.put(`/api/auth0/users/${currentUser.id}`, userData, { withCredentials: true });
        console.log('Update response:', response.data);
        // Refresh users list
        window.location.reload();
      } else {
        console.log('Making POST request to:', '/api/auth0/users');
        const response = await axios.post('/api/auth0/users', userData, { withCredentials: true });
        console.log('Create response:', response.data);

        // Show success message with password reset information
        if (response.data.passwordResetSent) {
          alert(`User created successfully!\n\n${response.data.instructions}\n\nThe user should check their email (including spam folder) for password setup instructions.`);
        } else {
          alert(`User created successfully!\n\n${response.data.instructions || 'User has been created with a temporary password.'}`);
        }

        const newUser = { ...userData, id: response.data.user?.id || response.data.id };
        setSelectedUser(newUser);
        // Update URL to include the new user ID
        navigate(`/users?id=${newUser.id}`);
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving user:', err);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);
      alert(`Error saving user: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    // Update URL to include the user ID
    navigate(`/users?id=${user.id}`);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await deleteUser(userId);

        // If the deleted user is currently selected, clear the selection
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(null);
          navigate('/users');
        }
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleOpenAssignProjectDialog = () => {
    setNewProjectAssignment({
      project_ids: [] // Reset to empty array
    });
    setJobNumberInput(''); // Reset job number input
    setOpenAssignProjectDialog(true);
  };

  const handleCloseAssignProjectDialog = () => {
    setOpenAssignProjectDialog(false);
    setJobNumberInput(''); // Reset job number input on close
  };

  const handleJobNumberChange = (e) => {
    const inputValue = e.target.value;
    setJobNumberInput(inputValue);
  };

  // Handle when user presses Enter in the job number field
  const handleJobNumberKeyPress = (e) => {
    if (e.key === 'Enter') {
      const inputValue = jobNumberInput.trim();

      if (inputValue === '') {
        return;
      }

      // Find projects that match the entered job number (exact or partial match)
      const matchingProjects = projects.filter(project =>
        project.job_number && project.job_number.toLowerCase().includes(inputValue.toLowerCase())
      );

      // Add matching projects to existing selections (avoid duplicates)
      if (matchingProjects.length > 0) {
        const currentProjectIds = newProjectAssignment.project_ids || [];
        const newProjectIds = matchingProjects.map(p => p.id);

        // Combine existing and new project IDs, removing duplicates
        const combinedProjectIds = [...new Set([...currentProjectIds, ...newProjectIds])];

        setNewProjectAssignment({
          project_ids: combinedProjectIds
        });

        // Clear the input field after adding projects so user can type next job number
        setJobNumberInput('');
      } else {
        alert(`No projects found with job number containing "${inputValue}"`);
      }
    }
  };

  const handleAssignProjects = async () => {
    if (!newProjectAssignment.project_ids || newProjectAssignment.project_ids.length === 0) {
      alert('Please select at least one project');
      return;
    }

    const results = {
      succeeded: [],
      alreadyAssigned: [],
      failed: []
    };

    // Assign each selected project individually
    for (const projectId of newProjectAssignment.project_ids) {
      try {
        console.log(`🔄 Assigning project ${projectId} to user ${selectedUser.id}`);
        await assignUserToProject(
          projectId,
          selectedUser.id,
          undefined, // Let backend use user's existing role from database
          false // Not setting as project lead
        );
        console.log(`✅ Successfully assigned project ${projectId}`);
        results.succeeded.push(projectId);
      } catch (err) {
        console.error(`❌ Error assigning project ${projectId}:`, err);
        console.error('Error details:', err.response?.data);

        // Check if it's an "already assigned" error
        if (err.response?.data?.error?.includes('already assigned')) {
          results.alreadyAssigned.push(projectId);
        } else {
          results.failed.push(projectId);
        }
      }
    }

    // Refresh user projects
    const updatedUserProjects = getUserProjects(selectedUser.id);
    setUserProjects(updatedUserProjects);
    handleCloseAssignProjectDialog();

    // Show results
    const messages = [];
    if (results.succeeded.length > 0) {
      messages.push(`✅ ${results.succeeded.length} project(s) assigned successfully`);
    }
    if (results.alreadyAssigned.length > 0) {
      messages.push(`ℹ️ ${results.alreadyAssigned.length} project(s) already assigned`);
    }
    if (results.failed.length > 0) {
      messages.push(`❌ ${results.failed.length} project(s) failed`);
    }

    if (messages.length > 0) {
      alert(messages.join('\n'));
    } else {
      alert('No projects were assigned.');
    }
  };

  const handleRemoveProjectAssignment = async (assignmentId) => {
    if (window.confirm('Are you sure you want to remove this project assignment?')) {
      try {
        await removeUserFromProject(assignmentId);

        // Refresh user projects
        const updatedUserProjects = getUserProjects(selectedUser.id);
        setUserProjects(updatedUserProjects);
        alert('Project assignment removed successfully!');
      } catch (err) {
        console.error('Error removing project assignment:', err);
        alert('Failed to remove project assignment. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading users...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography>
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Users
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ mr: 2 }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {selectedUser ? (
        <Box sx={{ width: '100%', mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5">{selectedUser.name}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
              <Typography variant="body2">Hourly Rate: {(selectedUser.hourly_rate ?? 0).toFixed(2)}</Typography>
              <Typography variant="body2">Role: {selectedUser.role}</Typography>
            </Box>
            <Button variant="outlined" onClick={() => setSelectedUser(null)}>
              Back to Users
            </Button>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="user tabs">
              <Tab label="Details" id="user-tab-0" />
              <Tab label="Projects" id="user-tab-1" />
              <Tab label="Timesheets" id="user-tab-2" />
            </Tabs>
          </Box>

          {/* Details Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>User Details</Typography>
              <Typography><strong>Name:</strong> {selectedUser.name}</Typography>
              <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
              <Typography><strong>Hourly Rate:</strong> {(selectedUser.hourly_rate ?? 0).toFixed(2)}</Typography>
              <Typography><strong>Job Role:</strong> {selectedUser.role}</Typography>
              <Typography><strong>System Permissions:</strong> {
                selectedUser.permissions === 'admin' ? 'Administrator Access' : 'Standard User Access'
              }</Typography>
              <Typography><strong>Department:</strong> {
                selectedUser.department_id
                  ? departments.find(d => d.id === selectedUser.department_id)?.name || 'Unknown Department'
                  : 'No Department Assigned'
              }</Typography>
              <Typography><strong>Project Lead:</strong> {selectedUser.project_lead === 1 ? 'Yes' : 'No'}</Typography>
              <Typography><strong>Created:</strong> {new Date(selectedUser.created_at).toLocaleDateString()}</Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenDialog(selectedUser)}
                >
                  Edit User
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteUser(selectedUser.id)}
                >
                  Delete User
                </Button>
              </Box>
            </Box>
          )}

          {/* Projects Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>Assigned Projects</Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenAssignProjectDialog}
                >
                  Assign Project
                </Button>
              </Box>

              {userProjects && userProjects.length > 0 ? (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Project Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userProjects.map((project) => (
                        <TableRow key={project.id}>
                          <TableCell>{project.name}</TableCell>
                          <TableCell>{selectedUser?.role || 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FolderIcon />}
                                href={`/projects?id=${project.id}`}
                              >
                                View Project
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleRemoveProjectAssignment(project.assignment_id)}
                              >
                                Remove
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 1 }}>
                  <Typography>
                    This user is not assigned to any projects yet.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Timesheets Tab */}
          {tabValue === 2 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Timesheets</Typography>
              <Typography>
                View and manage timesheets for this user.
              </Typography>
              {/* Timesheet functionality will be added later */}
              <Button
                variant="contained"
                href={`/time-logging?user_id=${selectedUser.id}`}
                sx={{ mt: 2 }}
              >
                View Timesheets
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Hourly Rate</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Project Lead</TableCell>
                <TableCell>Projects</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => handleUserSelect(user)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{(user.hourly_rate ?? 0).toFixed(2)}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    {user.department_id 
                      ? departments.find(d => d.id === user.department_id)?.name || 'Unknown'
                      : 'None'
                    }
                  </TableCell>
                  <TableCell>
                    {user.project_lead === 1 ? (
                      <Chip
                        label="Yes"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="No"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // Calculate project count for this user
                      const userProjectCount = projectUsers.filter(pu => pu.user_id === user.id).length;
                      return (
                        <Chip
                          label={`${userProjectCount} projects`}
                          size="small"
                          color={userProjectCount > 0 ? "primary" : "default"}
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (userProjectCount > 0) {
                              handleUserSelect(user);
                              setTabValue(1); // Switch to Projects tab
                            }
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(user);
                      }}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user.id);
                      }}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No users found. Add some users to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{isEditing ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={currentUser.name}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              name="email"
              type="email"
              value={currentUser.email}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Hourly Rate"
              name="hourly_rate"
              type="number"
              value={currentUser.hourly_rate}
              onChange={handleInputChange}
              inputProps={{ step: '0.01', min: '0' }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Role"
              name="role"
              select
              value={currentUser.role}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              <option value="Trainee Project Manager">Trainee Project Manager</option>
              <option value="Trainee Quantity Surveyor">Trainee Quantity Surveyor</option>
              <option value="Junior Project Manager">Junior Project Manager</option>
              <option value="Junior Quantity Surveyor">Junior Quantity Surveyor</option>
              <option value="Intermediate Project Manager">Intermediate Project Manager</option>
              <option value="Intermediate Quantity Surveyor">Intermediate Quantity Surveyor</option>
              <option value="Senior Project Manager">Senior Project Manager</option>
              <option value="Senior Quantity Surveyor">Senior Quantity Surveyor</option>
              <option value="Professional Project Manager">Professional Project Manager</option>
              <option value="Professional Quantity Surveyor">Professional Quantity Surveyor</option>
              <option value="Manager">Manager</option>
              <option value="Director">Director</option>
              <option value="Project Administrator">Project Administrator</option>
              <option value="General Administration">General Administration</option>
              <option value="administrator">System Administrator</option>
            </TextField>

            <FormControl fullWidth margin="normal">
              <InputLabel id="permissions-select-label">System Permissions</InputLabel>
              <Select
                labelId="permissions-select-label"
                name="permissions"
                value={currentUser.permissions || 'user'}
                onChange={handleInputChange}
                label="System Permissions"
              >
                <MenuItem value="user">Standard User Access</MenuItem>
                <MenuItem value="admin">Administrator Access</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="department-select-label">Department</InputLabel>
              <Select
                labelId="department-select-label"
                name="department_id"
                value={currentUser.department_id || ''}
                onChange={handleInputChange}
                label="Department"
              >
                <MenuItem value="">
                  <em>No Department</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentUser.is_project_lead}
                    onChange={handleInputChange}
                    name="is_project_lead"
                  />
                }
                label="Project Lead"
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Project Dialog */}
      <Dialog open={openAssignProjectDialog} onClose={handleCloseAssignProjectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Projects to {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              fullWidth
              options={projects.filter(project => {
                // Filter out projects already assigned to this user
                // ponytail: getUserProjects spreads the project, so the key is `id`, not `project_id`.
                return !userProjects.some(up => up.id === project.id);
              })}
              value={projects.filter(p => newProjectAssignment.project_ids.includes(p.id))}
              onChange={(_, newValue) => {
                setNewProjectAssignment({
                  project_ids: newValue.map(project => project.id)
                });
              }}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Projects"
                  placeholder="Search projects..."
                  margin="normal"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              sx={{
                '& .MuiAutocomplete-option': {
                  '&[aria-selected="true"]': {
                    backgroundColor: 'rgba(255, 152, 0, 0.3) !important', // Light orange
                    '&:hover': {
                      backgroundColor: 'rgba(255, 152, 0, 0.4) !important', // Slightly darker orange on hover
                    },
                  },
                },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Project Number"
              value={jobNumberInput}
              onChange={handleJobNumberChange}
              onKeyPress={handleJobNumberKeyPress}
              placeholder="Type job number and press Enter to add project..."
              helperText="Enter a job number and press Enter to add matching project(s)"
            />
            {newProjectAssignment.project_ids.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {newProjectAssignment.project_ids.length} project(s) selected
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignProjectDialog}>Cancel</Button>
          <Button
            onClick={handleAssignProjects}
            variant="contained"
            disabled={newProjectAssignment.project_ids.length === 0}
          >
            Assign {newProjectAssignment.project_ids.length > 0 ? `(${newProjectAssignment.project_ids.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;
